import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import Alert from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import Checkbox from '../components/ui/Checkbox';
import { TextArea } from '../components/ui/Field';
import Dialog from '../components/ui/Dialog';
import FadeIn from '../components/ui/FadeIn';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { errorMessage, getExport } from './api';
import { TONAL_SMALL } from './DocSection';
import type { PlanExport } from './types';


const PRIMARY_SMALL = buttonClass('filled', 'sm');

export interface ExportModalProps {
  planId: string;
  /** Shown while the export loads; the export's own title replaces it. */
  title: string;
  onClose: () => void;
}

type Load = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; data: PlanExport };

/** The plan as one markdown file: read it, copy it, or save it. */
const ExportModal: React.FC<ExportModalProps> = ({ planId, title, onClose }) => {
  const toast = useToast();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [copied, setCopied] = useState(false);
  const [includeDone, setIncludeDone] = useState(false);
  // A refetch for the checkbox keeps the old text on screen, marked busy, rather than the skeleton.
  const [refreshing, setRefreshing] = useState(false);
  const latest = useRef(0);
  const text = useRef<HTMLTextAreaElement>(null);

  const fetchExport = useCallback(async () => {
    const req = ++latest.current;
    setLoad((prev) => (prev.state === 'ready' ? prev : { state: 'loading' }));
    setRefreshing(true);
    try {
      const data = await getExport(planId, { done: includeDone });
      if (req === latest.current) setLoad({ state: 'ready', data });
    } catch (e) {
      if (req === latest.current) setLoad({ state: 'error', message: errorMessage(e, 'Could not build the export.') });
    } finally {
      if (req === latest.current) setRefreshing(false);
    }
  }, [planId, includeDone]);

  useEffect(() => {
    fetchExport();
    return () => {
      latest.current += 1;
    };
  }, [fetchExport]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(t);
  }, [copied]);

  // Copy and Download wait for the refetch: the text on screen may not match the checkbox yet.
  const ready = load.state === 'ready' && !refreshing;

  const copyAll = async () => {
    if (load.state !== 'ready' || refreshing) return;
    try {
      await navigator.clipboard.writeText(load.data.markdown);
      setCopied(true);
      toast.success('Copied');
    } catch {
      // No clipboard (an insecure origin, a denied permission): leave the text selected instead.
      text.current?.focus();
      text.current?.select();
      toast.error('Could not copy. The text is selected, so press Ctrl+C.');
    }
  };

  const download = () => {
    if (load.state !== 'ready' || refreshing) return;
    const url = URL.createObjectURL(new Blob([load.data.markdown], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = load.data.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoked on the next tick: some browsers start the download after click() returns.
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const heading = load.state === 'ready' ? load.data.title : title;

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={heading}
      meta={
        load.state === 'ready' ? (
          <>
            <span className="tabular-nums">rev {load.data.version}</span>, as markdown
          </>
        ) : (
          'Markdown export'
        )
      }
      closeHint="Close the export"
      footer={
        <>
          <Checkbox
            label="Include done topics"
            checked={includeDone}
            onChange={(e) => setIncludeDone(e.target.checked)}
            className="mr-auto min-h-[32px]"
          />
          <button type="button" onClick={download} disabled={!ready} className={TONAL_SMALL}>
            Download .md
          </button>
          <button type="button" onClick={copyAll} disabled={!ready} className={`${PRIMARY_SMALL} min-w-[96px]`}>
            {copied ? (
              <>
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none">
                  <path
                    d="M5 12.5l4.5 4.5L19 7.5"
                    stroke="currentColor"
                    strokeWidth={2.4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Copied
              </>
            ) : (
              'Copy all'
            )}
          </button>
        </>
      }
    >
      <FadeIn key={load.state}>
      {load.state === 'loading' && (
        <div aria-busy="true" className="h-[min(60dvh,36rem)] rounded-lg bg-surface-container-highest p-4">
          <SkeletonGroup label="Building the export" className="space-y-2">
            {[72, 48, 88, 64, 80, 40].map((w, i) => (
              <div key={i} style={{ width: `${w}%` }}>
                <Skeleton className="h-3 rounded-full" />
              </div>
            ))}
          </SkeletonGroup>
        </div>
      )}
      {load.state === 'error' && <Alert onRetry={fetchExport}>{load.message}</Alert>}
      {load.state === 'ready' && (
        <TextArea
          ref={text}
          readOnly
          spellCheck={false}
          value={load.data.markdown}
          aria-label="Plan as markdown"
          aria-busy={refreshing}
          className="h-[min(60dvh,36rem)] !resize-none !p-4 font-mono !text-[12.5px] !leading-relaxed"
        />
      )}
      </FadeIn>
    </Dialog>
  );
};

export default ExportModal;
